import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flag, Ban } from "lucide-react";
import { API } from "@/lib/api";

const nomsOpen = (e) =>
  !e.cancelled && e.nominations_open !== false && !(e.nomination_cutoff && new Date(e.nomination_cutoff) < new Date());

const fmtDateLong = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export default function NomsOpenBanner({ onNominate }) {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    fetch(`${API}/events`)
      .then((r) => r.json())
      .then((d) => {
        if (!Array.isArray(d)) return;
        const today = new Date().toLocaleDateString("en-CA");
        const next = d.filter((e) => e.date >= today && nomsOpen(e)).sort((a, b) => a.date.localeCompare(b.date))[0];
        setEvent(next || null);
      })
      .catch(() => {});
  }, []);

  if (!event) return null;

  return (
    <section data-testid="noms-open-banner" className="relative border-y border-clay-border bg-clay-surface/60 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-12 sm:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-clay-gold mb-4">{event.title}</p>
          <h2
            data-testid="noms-open-title"
            className="font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl mb-5"
          >
            Nominations<br />
            <span className="text-clay-orange">Now Open</span>
          </h2>
          <p data-testid="noms-open-date" className="font-display font-bold uppercase tracking-wide text-xl sm:text-2xl text-clay-text mb-2">
            {fmtDateLong(event.date)}
          </p>
          {event.classes && (
            <p className="text-clay-muted text-sm mb-8 max-w-lg">{event.classes}</p>
          )}
          <div className="flex flex-wrap items-center gap-4">
            <button
              data-testid="noms-open-cta"
              onClick={() => onNominate?.(event.id)}
              className="inline-flex items-center gap-3 bg-clay-orange hover:bg-clay-gold text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-10 py-4 transition-all duration-300 hover:-translate-y-0.5 clay-glow"
            >
              <Flag className="w-5 h-5" /> Nominate Now
            </button>
            <Link
              data-testid="withdraw-nomination-button"
              to={`/withdraw-nomination?event_id=${event.id}`}
              className="inline-flex items-center gap-2 border border-clay-border hover:border-red-500/60 hover:text-red-400 text-clay-muted font-display font-bold uppercase tracking-wider text-sm px-5 py-3 transition-colors"
            >
              <Ban className="w-4 h-4" /> Withdraw Nomination
            </Link>
          </div>
        </motion.div>

        {event.poster && (
          <motion.figure
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="justify-self-center lg:justify-self-end"
          >
            <img
              data-testid="noms-open-poster"
              src={event.poster}
              alt={`${event.title} poster`}
              className="w-full max-w-sm border border-clay-border shadow-2xl shadow-black/60"
            />
          </motion.figure>
        )}
      </div>
    </section>
  );
}
